import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { usePageMeta } from "@/hooks/usePageMeta";
import SubmissionsPanel from "@/components/SubmissionsPanel";

const SYMBOL = `${process.env.PUBLIC_URL}/amanah-symbol.png`;

export default function SiteSettingsPage() {
  usePageMeta("Site Settings | Amanah Promotions");
  const { user, logout } = useAuth();
  const { refresh } = useSettings();

  const [form, setForm] = useState({
    phone: "",
    phone_active: false,
    business_hours: "",
    public_email: "",
  });
  // Last successfully persisted state — drives the public-status indicator.
  const [saved, setSaved] = useState({ phone: null, phone_active: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/settings");
    setForm({
      phone: data.phone || "",
      phone_active: !!data.phone_active,
      business_hours: data.business_hours || "",
      public_email: data.public_email || "",
    });
    setSaved({ phone: data.phone || null, phone_active: !!data.phone_active });
  }, []);

  useEffect(() => {
    (async () => {
      try {
        await load();
      } catch (e) {
        toast.error("Could not load settings.");
      } finally {
        setLoading(false);
      }
    })();
  }, [load]);

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/settings", form);
      await load(); // reflect the actual persisted (server-normalised) state
      await refresh();
      toast.success("Settings saved.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save settings.");
    } finally {
      setSaving(false);
    }
  };

  const phoneLive = saved.phone_active && !!saved.phone;

  return (
    <div className="min-h-screen bg-cream">
      <header className="border-b border-line bg-white">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-3">
            <img src={SYMBOL} alt="Amanah" className="h-9 w-auto" />
            <span className="font-display text-base font-medium text-navy">Site Settings</span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="hidden text-sm text-slate-muted sm:inline" data-testid="admin-user-email">
              {user?.email}
            </span>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 border border-line px-4 py-2 text-sm font-medium text-navy transition-colors hover:bg-navy hover:text-white"
              data-testid="admin-logout"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-6 py-12">
        <h1 className="font-display text-3xl font-medium tracking-tight text-navy sm:text-4xl">
          Public contact details
        </h1>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-body">
          These details are stored once and shown consistently across the website. The telephone
          number only appears publicly when a valid number is saved and set to display. The
          placeholder is never shown to visitors.
        </p>

        {loading ? (
          <div className="mt-10 flex items-center gap-3 text-sm text-slate-muted">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-line border-t-brand-green" />
            Loading settings…
          </div>
        ) : (
          <form onSubmit={onSave} className="mt-10 space-y-8" noValidate>
            <div className="border border-line bg-white p-6 sm:p-8">
              <label htmlFor="phone" className="mb-2 block text-sm font-medium text-navy">
                Public telephone number
              </label>
              <Input
                id="phone"
                type="tel"
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                placeholder="e.g. +44 20 7946 0958"
                className="h-12 max-w-sm rounded-none border-line bg-white text-navy focus-visible:ring-2 focus-visible:ring-brand-green md:text-base"
                data-testid="settings-phone"
              />
              <p className="mt-2 text-xs text-slate-muted">
                Enter 10–15 digits. Leave blank to keep the number hidden.
              </p>

              <div className="mt-6 flex items-center gap-3">
                <Switch
                  id="phone_active"
                  checked={form.phone_active}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, phone_active: v }))}
                  className="data-[state=checked]:bg-brand-green"
                  data-testid="settings-phone-active"
                />
                <label htmlFor="phone_active" className="text-sm font-medium text-navy">
                  Display this number publicly
                </label>
              </div>

              <div
                className={`mt-6 inline-flex items-center gap-2 px-4 py-2 text-sm ${
                  phoneLive ? "bg-brand-green/10 text-brand-greenDeep" : "bg-cream text-slate-muted"
                }`}
                data-testid="settings-phone-status"
              >
                {phoneLive ? (
                  <>
                    <Check className="h-4 w-4" /> Showing publicly: {saved.phone}
                  </>
                ) : (
                  <>Telephone is currently hidden on the public website.</>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div className="border border-line bg-white p-6 sm:p-8">
                <label htmlFor="hours" className="mb-2 block text-sm font-medium text-navy">
                  Business hours
                </label>
                <Input
                  id="hours"
                  value={form.business_hours}
                  onChange={(e) => setForm((f) => ({ ...f, business_hours: e.target.value }))}
                  className="h-12 rounded-none border-line bg-white text-navy focus-visible:ring-2 focus-visible:ring-brand-green md:text-base"
                  data-testid="settings-hours"
                />
              </div>
              <div className="border border-line bg-white p-6 sm:p-8">
                <label htmlFor="pemail" className="mb-2 block text-sm font-medium text-navy">
                  Public email
                </label>
                <Input
                  id="pemail"
                  type="email"
                  value={form.public_email}
                  onChange={(e) => setForm((f) => ({ ...f, public_email: e.target.value }))}
                  className="h-12 rounded-none border-line bg-white text-navy focus-visible:ring-2 focus-visible:ring-brand-green md:text-base"
                  data-testid="settings-email"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 bg-brand-green px-8 py-3.5 text-base font-semibold text-white transition-colors hover:bg-brand-greenDeep disabled:opacity-60"
              data-testid="settings-save"
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
          </form>
        )}

        <SubmissionsPanel />
      </main>
    </div>
  );
}
